import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Wrapper from '../common/Wrapper'
import DataSet from 'common-ui/components/DataSet'
import GetApp from '@mui/icons-material/GetApp'

import { findProductById } from 'common/constants'
import { useBillingApi } from '../../api/billing/billing-context'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { BillingSummary } from '../common/BillingSummary'

import { findLicenseTypeById } from 'common-billing-server/index'
import type { BillingReport } from 'common-billing-server/types'
import { DateRangePicker } from '../common/DateRangePicker'

export const BillingDetailsPage = () => {
    const billingApi = useBillingApi()
    const [billingReport, setBillingReport] = useState<BillingReport | undefined>()
    const customer = billingReport?.periods[0]?.customer
    const license = billingReport?.periods[0]
        ? {
              productName: findProductById(billingReport.periods[0].license.product)?.name,
              licenseTypeName: findLicenseTypeById(billingReport.periods[0].license.licenseType)?.name,
          }
        : undefined
    const [isLoading, setIsLoading] = useState(false)
    const [licenseKey, setLicenseKey] = useState<string | undefined>()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    const [searchParams, setSearchParams] = useSearchParams()
    const licenseId = searchParams.get('licenseId')
    const startDateUrlParam = searchParams.get('startDate')
    const endDateUrlParam = searchParams.get('endDate')

    const setDate = (key: 'startDate' | 'endDate', value: Date | null) => {
        if (!value || !value.getTime()) return

        searchParams.set(key, value.toISOString())
        setSearchParams(searchParams, { replace: true })
    }

    useEffect(() => {
        async function fetchBillingReport() {
            const operationId = 'fetch-billing-report'
            if (!licenseId) return
            if (!startDateUrlParam) return
            if (!endDateUrlParam) return

            removeFailedOperation(operationId)
            setBillingReport(undefined)
            setIsLoading(true)
            try {
                const billingReport = await billingApi.getLicenseBillingReport(licenseId, {
                    startDate: new Date(startDateUrlParam).toISOString(),
                    endDate: new Date(endDateUrlParam).toISOString(),
                })
                setBillingReport(billingReport)
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching billing report',
                    error,
                    retryFn: () => void fetchBillingReport(),
                })
            }
            setIsLoading(false)
        }

        void fetchBillingReport()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [licenseId, startDateUrlParam, endDateUrlParam])

    useEffect(() => {
        if (!licenseId) return
        void billingApi
            .listLicenses({ filter: { licenseIds: [licenseId] } })
            .then((res) => setLicenseKey(res.items[0]?.licenseKey))
            .catch(() => undefined)
    }, [billingApi, licenseId])

    function makeBillingReportPdfUrl(licenseId: string, startDate: string, endDate: string) {
        const searchParams = new URLSearchParams()
        searchParams.set('startDate', startDate)
        searchParams.set('endDate', endDate)
        searchParams.set('format', 'pdf')
        return `/api/license/${licenseId}/billing/report?${searchParams.toString()}`
    }

    return (
        <Wrapper title={licenseKey}>
            <Paper sx={{ p: 2 }}>
                <DataSet
                    values={{
                        ...(customer && {
                            Customer: customer.name,
                            'Customer number': customer.customerNumber,
                        }),
                        ...(licenseId && { 'License ID': licenseId }),
                        ...(license && {
                            Product: license.productName,
                            'License type': license.licenseTypeName,
                        }),
                    }}
                />
            </Paper>

            {startDateUrlParam && endDateUrlParam && (
                <Paper sx={{ paddingTop: 1, mt: 2 }}>
                    <div
                        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', padding: '20px' }}
                    >
                        <DateRangePicker
                            startDateString={startDateUrlParam}
                            endDateString={endDateUrlParam}
                            onChange={setDate}
                        />
                        {isLoading && <CircularProgress style={{ marginLeft: 12, padding: '8px' }} />}
                        {licenseId && (
                            <Link
                                underline="none"
                                href={makeBillingReportPdfUrl(licenseId, startDateUrlParam, endDateUrlParam)}
                            >
                                <Button variant="outlined" startIcon={<GetApp />}>
                                    Download
                                </Button>
                            </Link>
                        )}
                    </div>
                    {billingReport && <BillingSummary billing={billingReport} />}
                </Paper>
            )}

            {errorBanner}
        </Wrapper>
    )
}
